import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled, { keyframes, css } from 'styled-components';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { FiArrowLeft, FiCheck, FiClock, FiAlertTriangle, FiCheckCircle, FiFileText, FiCpu, FiDatabase, FiLayers, FiUserCheck, FiGitMerge, FiSearch, FiStar, FiInfo } from 'react-icons/fi';
import config from '../config';
import { CircularProgress } from '@mui/material';
import { Alert } from '@mui/material';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import AccellorLoading from '../components/AccellorLoading';

// Define light theme color palette
const lightTheme = {
  pageBackground: '#F3F4F6', // Light gray background
  cardBackground: '#FFFFFF', // White cards
  primaryText: '#1F2937',    // Dark gray for primary text
  secondaryText: '#4B5563',  // Medium gray for secondary/labels
  accentColor: '#2563EB',    // A nice blue for accents (e.g., buttons, active states)
  accentColorGreen: '#059669', // Green for success/completed states
  borderColor: '#E5E7EB',    // Light gray for borders
  shadowColor: 'rgba(0, 0, 0, 0.05)', // Softer shadow for light theme
  tableHeaderBg: '#F9FAFB', // Very light gray for table headers
  iconColor: '#4B5563',
  disabledColor: '#9CA3AF',
  pulseColor: 'rgba(37, 99, 235, 0.4)', // Blue pulse
};

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background: ${lightTheme.pageBackground};
  color: ${lightTheme.primaryText};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${lightTheme.secondaryText};
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: ${lightTheme.primaryText};
    background-color: ${lightTheme.borderColor};
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  color: ${lightTheme.primaryText};
`;

const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2rem 0;
  position: relative;
  padding: 0 2rem;

  &::before {
    content: '';
    position: absolute;
    top: 2rem;
    left: 3rem;
    right: 3rem;
    height: 2px;
    background: ${lightTheme.borderColor};
    z-index: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 120px;

  .icon-container {
    width: 4rem;
    height: 4rem;
    background: ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.pageBackground};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
    border: 2px solid ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.borderColor};
    
    ${props => props.active && `
      box-shadow: 0 0 0 4px ${lightTheme.pulseColor};
      animation: pulse 2s infinite;
    `}

    svg {
      color: ${props => props.active || props.completed ? '#fff' : lightTheme.secondaryText};
      font-size: 1.5rem;
    }
  }

  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.secondaryText};
    text-align: center;
    margin-top: 0.5rem;
  }

  .sublabel {
    font-size: 0.75rem;
    color: ${lightTheme.secondaryText};
    text-align: center;
    margin-top: 0.25rem;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 ${lightTheme.pulseColor};
    }
    70% {
      box-shadow: 0 0 0 10px rgba(37, 99, 235, 0);
    }
    100% {
      box-shadow: 0 0 0 0 ${lightTheme.pulseColor};
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
`;

const Card = styled.div`
  background: ${lightTheme.cardBackground};
  border-radius: 0.5rem;
  padding: 1.25rem;
  border: 1px solid ${lightTheme.borderColor};
  box-shadow: 0 2px 4px ${lightTheme.shadowColor}, 0 1px 2px ${lightTheme.shadowColor};
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: box-shadow 0.3s ease-out;

  &:hover {
    box-shadow: 0 5px 15px ${lightTheme.shadowColor}, 0 2px 6px ${lightTheme.shadowColor};
  }

  h2 {
    font-size: 1rem;
    font-weight: 500;
    margin: 0 0 1rem 0;
    color: ${lightTheme.primaryText};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const AgentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.active ? lightTheme.accentColor + '20' : lightTheme.cardBackground};
  border: 1px solid ${props => props.active ? lightTheme.accentColor : lightTheme.borderColor};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  transition: all 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${props => props.clickable ? lightTheme.borderColor : props.active ? lightTheme.accentColor + '20' : lightTheme.cardBackground};
    border-color: ${props => props.clickable ? lightTheme.accentColor : props.active ? lightTheme.accentColor : lightTheme.borderColor};
  }

  .icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: ${props => {
      if (props.status === 'completed') return lightTheme.accentColorGreen;
      if (props.status === 'processing') return lightTheme.accentColor;
      if (props.status === 'failed') return '#DC2626';
      return lightTheme.secondaryText;
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .content {
    flex: 1;

    h3 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${lightTheme.primaryText};
    }

    p {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      color: ${lightTheme.secondaryText};
    }
  }

  .status {
    font-size: 0.75rem;
    color: ${props => {
      if (props.status === 'completed') return lightTheme.accentColorGreen;
      if (props.status === 'processing') return lightTheme.accentColor;
      if (props.status === 'failed') return '#F87171';
      return lightTheme.secondaryText;
    }};
  }

  .progress-indicator {
    position: absolute;
    left: -2.25rem;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${props => props.active ? lightTheme.accentColorGreen : props.completed ? lightTheme.accentColorGreen : lightTheme.borderColor};
    border: 2px solid ${props => props.active ? lightTheme.accentColorGreen : props.completed ? lightTheme.accentColorGreen : lightTheme.secondaryText};
    z-index: 2;
  }

  ${props => props.active && css`
    .progress-indicator {
      animation: ${pulseAnimation} 2s infinite;
    }
  `}
`;

const Table = styled.div`
  margin-top: 1rem;

  .table-section {
    margin-bottom: 2rem;

    h3 {
      font-size: 1rem;
      font-weight: 500;
      color: ${lightTheme.secondaryText};
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid ${lightTheme.borderColor};
    }

    table {
      width: 100%;
      border-collapse: collapse;

      th, td {
        padding: 0.75rem;
        text-align: left;
        border-bottom: 1px solid ${lightTheme.borderColor};
        font-size: 0.875rem;
      }

      th {
        color: ${lightTheme.secondaryText};
        font-weight: 500;
        background-color: ${lightTheme.tableHeaderBg};
      }

      td {
        color: ${lightTheme.primaryText};
      }

      .confidence {
        color: ${lightTheme.accentColorGreen};
      }
    }
  }
`;

const StatusCard = styled.div`
  background: ${lightTheme.cardBackground};
  border: 1px solid ${lightTheme.borderColor};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px ${lightTheme.shadowColor}, 0 2px 4px -1px ${lightTheme.shadowColor};

  .header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .icon {
    color: ${lightTheme.accentColor};
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 500;
    color: ${lightTheme.primaryText};
    margin: 0;
  }

  .value {
    font-size: 1.875rem;
    font-weight: 700;
    color: ${lightTheme.primaryText};
    margin-top: 0.5rem;
  }
`;

const DetailCard = styled.div`
  background: ${lightTheme.cardBackground};
  border: 1px solid ${lightTheme.borderColor};
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px ${lightTheme.shadowColor}, 0 2px 4px -1px ${lightTheme.shadowColor};

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 500;
    color: ${lightTheme.primaryText};
    margin: 0;
  }

  .confidence {
    font-size: 0.875rem;
    color: ${lightTheme.secondaryText};
  }

  .value {
    font-size: 1rem;
    color: ${lightTheme.primaryText};
    font-weight: 400;
    margin-top: 0.5rem;
    line-height: 1.6;
  }
`;

const MainContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 1.5rem;
  background: ${lightTheme.pageBackground};
  color: ${lightTheme.primaryText};
  min-height: 100vh;
  font-family: 'Inter', sans-serif;

  h1, h2, h3, p, th, td {
    color: inherit;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${lightTheme.primaryText};
  margin: 0;
  flex-grow: 1;
`;

const ProgressStepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  padding: 1rem 0;
  background: ${lightTheme.cardBackground};
  border-radius: 0.5rem;
  padding: 1.5rem 2rem;
  border: 1px solid ${lightTheme.borderColor};
`;

const StepProgress = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2rem;
  position: relative;
  padding: 1rem;
  background: ${lightTheme.cardBackground};
  border-radius: 0.5rem;
  border: 1px solid ${lightTheme.borderColor};
  box-shadow: 0 1px 3px ${lightTheme.shadowColor};
`;

const StepItem = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  padding: 0 0.25rem;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: -50%;
    top: 1.25rem;
    width: 100%;
    height: 2px;
    background: ${props => props.completed ? lightTheme.accentColorGreen : lightTheme.borderColor};
    z-index: 1;
    transform: translateX(-0.125rem);
  }

  .step-icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.pageBackground};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.5rem;
    position: relative;
    z-index: 2;
    border: 2px solid ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.borderColor};
    
    svg {
      font-size: 1.1rem;
      color: ${props => props.active || props.completed ? '#FFFFFF' : lightTheme.secondaryText};
    }

    ${props => props.active && css`
      animation: ${pulseAnimation} 2s infinite;
    `}
  }

  .step-label {
    font-size: 0.75rem;
    color: ${props => props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.secondaryText};
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .step-description {
    font-size: 0.65rem;
    color: ${lightTheme.secondaryText};
    max-width: 100px;
    margin: 0 auto;
    line-height: 1.3;
  }
`;

const MainLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${lightTheme.primaryText};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.6rem;

  svg {
    font-size: 1.2rem;
    color: ${lightTheme.accentColor};
  }
`;

const AgentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
  background: ${lightTheme.pageBackground};
  border-radius: 0.375rem;
  border: 1px solid ${lightTheme.borderColor};
  position: relative;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: #E9ECEF;
    transform: translateY(-1px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }

  .agent-info {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    flex: 1;
  }

  .agent-icon {
    width: 2.35rem;
    height: 2.35rem;
    border-radius: 50%;
    background: ${props => (props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.borderColor) + '20'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => (props.active ? lightTheme.accentColor : props.completed ? lightTheme.accentColorGreen : lightTheme.secondaryText)};
    svg { font-size: 1rem; }
  }

  .agent-name {
    font-weight: 500;
    font-size: 0.85rem;
    color: ${lightTheme.primaryText};
    margin-bottom: 0.1rem;
  }

  .agent-detail {
    font-size: 0.75rem;
    color: ${lightTheme.secondaryText};
  }

  .agent-status {
    padding: 0.25rem 0.6rem;
    border-radius: 1rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
    
    &.completed { color: #047857; background-color: #D1FAE5; border: 1px solid #A7F3D0; }
    &.active { color: #1E40AF; background-color: #DBEAFE; border: 1px solid #BFDBFE;}
    &.pending { color: #92400E; background-color: #FEF3C7; border: 1px solid #FDE68A; }
    &.failed { color: #991B1B; background-color: #FEE2E2; border: 1px solid #FECACA; }
  }
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.7rem 0;
  border-bottom: 1px solid ${lightTheme.borderColor};
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }

  span:first-child {
    color: ${lightTheme.secondaryText};
    white-space: nowrap;
    margin-right: 1rem;
  }

  span:last-child {
    color: ${lightTheme.primaryText};
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }
`;

const CircularProgressWrapper = styled.div`
  width: 110px;
  margin: 0.75rem auto 1.25rem;
`;

const VerifyButton = styled.button`
  background-color: ${lightTheme.accentColor};
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.6rem 1.25rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    background-color: #1D4ED8;
  }
`;

const TableContainer = styled.div`
  background: ${lightTheme.cardBackground};
  border-radius: 0.375rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border: 1px solid ${lightTheme.borderColor};
`;

const ScrollableTableWrapper = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  position: relative;
  max-height: 260px;
  padding-right: 0.1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${lightTheme.pageBackground};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${lightTheme.borderColor};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${lightTheme.secondaryText};
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background: ${lightTheme.tableHeaderBg};
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${lightTheme.secondaryText};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid ${lightTheme.borderColor};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${lightTheme.borderColor};
    color: ${lightTheme.primaryText};
    font-size: 0.8rem;
  }

  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover {
    background-color: ${lightTheme.pageBackground};
  }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 ${lightTheme.pulseColor}; }
  70% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
  100% { box-shadow: 0 0 0 0 ${lightTheme.pulseColor}; }
`;

const processSteps = [
  { id: 'upload', label: 'Upload', icon: <FiFileText />, description: 'Document uploaded and validated' },
  { id: 'processing', label: 'Processing', icon: <FiCpu />, description: 'AI agents analyzing document' },
  { id: 'verification', label: 'Human Verification', icon: <FiUserCheck />, description: 'Expert verification' },
  { id: 'ml_inference', label: 'ML Inference', icon: <FiGitMerge />, description: 'Machine learning analysis' },
  { id: 'rating', label: 'Rating', icon: <FiStar />, description: 'Final document rating' },
];

const VerticalProgressContainer = styled.div`
  position: relative;
  padding-left: 1.5rem;
  margin-left: calc(2.35rem / 2);
  flex-grow: 1;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.5rem;
    bottom: 0.5rem;
    width: 2px;
    background: ${lightTheme.borderColor};
    z-index: 0;
  }
`;

const VerticalProgressBar = styled.div`
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 2px;
  background: ${lightTheme.accentColorGreen};
  transition: height 0.3s ease;
  z-index: 1;
`;

const AgentsList = styled.div`
  position: relative;
  z-index: 2;
`;

const RatingStars = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1.25rem;

  .star svg {
    &.filled {
      color: #F59E0B;
      fill: #F59E0B;
    }
    &.empty {
      color: ${lightTheme.disabledColor};
    }
  }
`;

const DetailedDocumentDashboard = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confidenceScore, setConfidenceScore] = useState(0);

  useEffect(() => {
    const fetchAllDetails = async () => {
    try {
      setLoading(true);
      setError(null);

        // 1. Fetch document list from /populate-ui
        const allDocsResponse = await axios.get(`${config.API_BASE_URL}/populate-ui`);
        const currentDocument = allDocsResponse.data.find(doc => doc.document_id === documentId);
        
        if (!currentDocument) {
          throw new Error('Document not found in populate-ui');
        }
        setDocument(currentDocument);

        // 2. Fetch processing results if document is not pending
        // (Assuming 'pending' means initial upload before any processing starts)
        if (currentDocument.status !== 'pending' && currentDocument.status !== 'failed') { // Only fetch results if processed/completed
          try {
        const resultsResponse = await axios.get(`${config.API_BASE_URL}/get-results/${documentId}`);
        if (resultsResponse.data && resultsResponse.data.merged_content) {
          setResults(resultsResponse.data);
          
              // Calculate average confidence score
          const confidenceScores = [];
          const content = resultsResponse.data.merged_content;
          
          if (content.shares) {
            content.shares.forEach(share => {
                  Object.values(share).forEach(val => {
                    if (val && typeof val === 'object' && val.confidence) {
                       confidenceScores.push(val.confidence);
                    }
                  });
                });
              }
          if (content.subfunds) {
            content.subfunds.forEach(subfund => {
                     Object.values(subfund).forEach(val => {
                        if (val && typeof val === 'object' && val.confidence) {
                            confidenceScores.push(val.confidence);
                        }
                    });
                });
              }
              const otherFields = ['Fund Auditor', 'Umbrella Name', 'Transfer agent', 'DepositaryDetails', 'AdministratorDetails'];
          otherFields.forEach(field => {
            if (content[field]?.confidence) {
              confidenceScores.push(content[field].confidence);
            }
          });
          
          const averageConfidence = confidenceScores.length > 0
            ? confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length
            : 0;
          setConfidenceScore(Math.round(averageConfidence * 100));
            } else {
              // No merged_content, maybe set results to null or empty object
              setResults(null); 
            }
          } catch (resultsError) {
            // If /get-results fails (e.g. 404 for a document still processing), log it but don't block UI
            console.warn('Error fetching processing results /get-results:', resultsError);
            setResults(null); 
          }
        } else {
             setResults(null); // No results if document is pending or failed
        }

      } catch (err) {
        console.error('Error fetching document details:', err);
        setError(err.message || 'Failed to fetch document details');
        setDocument(null); // Clear document if main fetch fails
        setResults(null);
    } finally {
      setLoading(false);
    }
  };

    fetchAllDetails();
  }, [documentId]);

  const getCurrentStepStatus = (stepId, docStatus, isVerified, mlStatus, rating) => {
    const docStatusLower = docStatus?.toLowerCase();

    // Determine completion status of each conceptual phase
    const uploadCompleted = docStatusLower !== 'pending';
    const processingCompleted = uploadCompleted && (docStatusLower === 'completed' || docStatusLower === 'failed');
    const verificationCompleted = processingCompleted && docStatusLower === 'completed' && isVerified;
    // ML Inference is considered complete if verification is done and ML status is available.
    // If mlStatus is an object with a 'status' field, check that. Otherwise, check if it's just present.
    // For now, let's assume if mlStatus is truthy, it's done for the progress bar step.
    const mlInferenceCompleted = verificationCompleted && !!mlStatus;
    const ratingCompleted = mlInferenceCompleted && rating != null && rating > 0;

    switch (stepId) {
      case 'upload':
        return { 
          active: docStatusLower === 'pending', 
          completed: uploadCompleted 
        };
      case 'processing': // This covers all backend AI agent processing
        return { 
          active: uploadCompleted && !processingCompleted && docStatusLower !== 'failed', // Active if upload is done but main processing isn't finished (and not failed)
          completed: processingCompleted 
        };
      case 'verification': // Human Verification
        return { 
          active: processingCompleted && docStatusLower === 'completed' && !isVerified, // Active if processing is done, doc is 'completed', but not verified
          completed: verificationCompleted 
        };
      case 'ml_inference': // Represents the ML analysis phase, which might be post-verification or part of final results
        return { 
          active: verificationCompleted && !mlInferenceCompleted && docStatusLower === 'completed', // Active if verified but ML results/rating not yet fully set
          completed: mlInferenceCompleted 
        };
      case 'rating': // Final document rating step
        return { 
          active: mlInferenceCompleted && !ratingCompleted && docStatusLower === 'completed', // Active if ML is done but rating not yet final
          completed: ratingCompleted 
        };
      default:
        return { active: false, completed: false };
    }
  };
  
  // Placeholder for agent data - replace with API data when available
  const agents = [
    { 
      id: 'doc_preprocess', 
      name: 'Document Preprocessing Agent', 
      detail: 'Document cleaning and preparation', 
      icon: <FiFileText/>, 
      statusKey: 'preprocessing' 
    },
    { 
      id: 'doc_chunking', 
      name: 'Document Chunking Agent', 
      detail: 'Breaking document into processable chunks', 
      icon: <FiLayers/>, 
      statusKey: 'chunking' 
    },
    { 
      id: 'llm_processing', 
      name: 'LLM Processing Agent', 
      detail: 'Large Language Model analysis', 
      icon: <FiCpu/>, 
      statusKey: 'processing' 
    },
    { 
      id: 'response_finalization', 
      name: 'Response Finalization Agent', 
      detail: 'Finalizing and validating results', 
      icon: <FiCheckCircle/>, 
      statusKey: 'completed_pre_verification' 
    }
  ];

  const getAgentStatus = (agentStatusKey) => {
    if (!document || !document.status) return 'pending';
    const docStatus = document.status.toLowerCase();

    if (docStatus === 'failed') return 'failed';

    switch (agentStatusKey) {
        case 'preprocessing':
            return docStatus === 'pending' ? 'active' : (['preprocessing', 'chunking', 'processing', 'completed'].includes(docStatus) ? 'completed' : 'pending');
        case 'chunking':
            return docStatus === 'preprocessing' ? 'active' : (['chunking', 'processing', 'completed'].includes(docStatus) ? 'completed' : 'pending');
        case 'processing':
            return docStatus === 'chunking' ? 'active' : (['processing', 'completed'].includes(docStatus) ? 'completed' : 'pending');
        case 'completed_pre_verification': // This agent is considered completed if the document status is 'completed'
             return docStatus === 'processing' ? 'active' : (docStatus === 'completed' ? 'completed' : 'pending');
        default:
            return 'pending';
    }
  };

  if (loading) {
    return (
      <MainContainer style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '1rem', minHeight: 'calc(100vh - 4rem)' }}>
        <AccellorLoading size={80} />
        <p style={{ fontSize: '1.125rem', color: lightTheme.secondaryText, marginTop: '1rem' }}>Loading document details...</p>
      </MainContainer>
    );
  }

  if (error || !document) {
    return (
      <MainContainer style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '1rem', textAlign: 'center' }}>
        <FiAlertTriangle size={48} style={{ color: '#DC2626' }} />
        <p style={{ fontSize: '1.25rem', color: lightTheme.primaryText }}>{error || 'Document could not be loaded.'}</p>
        <BackButton onClick={() => navigate('/')}>
          <FiArrowLeft /> Back to Dashboard
        </BackButton>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <HeaderBar>
        <BackButton onClick={() => navigate('/')}> 
          <FiArrowLeft />
        </BackButton>
        <PageTitle>Document: {document?.document_name || 'Details'}</PageTitle>
      </HeaderBar>

      <StepProgress>
        {processSteps.map((step) => {
          const status = getCurrentStepStatus(step.id, document.status, document.verified, document.ml_inference_status, document.rating);
          return (
            <StepItem key={step.id} active={status.active} completed={status.completed}>
              <div className="step-icon">{step.icon}</div>
              <div className="step-label">{step.label}</div>
              <div className="step-description">{step.description}</div>
            </StepItem>
          );
        })}
      </StepProgress>
      
      <MainLayout>
        <TopRow>
          {/* Card 1: Key Extracted Information */}
          <Card>
            <CardTitle><FiInfo /> Key Extracted Information</CardTitle>
            {results?.merged_content ? (
              <>
                <InfoItem><span>Umbrella Fund:</span> <span>{results.merged_content['Umbrella Name']?.value || 'N/A'}</span></InfoItem>
                <InfoItem><span>Fund Auditor:</span> <span>{results.merged_content['Fund Auditor']?.value || 'N/A'}</span></InfoItem>
                <InfoItem><span>Administrator:</span> <span>{results.merged_content['AdministratorDetails']?.value || 'N/A'}</span></InfoItem>
                <InfoItem><span>Depositary:</span> <span>{results.merged_content['DepositaryDetails']?.value || 'N/A'}</span></InfoItem>
                <InfoItem><span>Transfer Agent:</span> <span>{results.merged_content['Transfer agent']?.value || 'N/A'}</span></InfoItem>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: lightTheme.secondaryText, flexGrow:1, display:'flex', alignItems:'center', justifyContent:'center' }}>No extraction data available.</div>
            )}
          </Card>

          {/* Card 2: Document Info */}
          <Card>
            <CardTitle><DescriptionOutlined style={{ fontSize: '1.3rem', color: lightTheme.accentColor }} /> Document Info</CardTitle>
            <InfoItem>
              <span>Document Type:</span> 
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: lightTheme.primaryText }}>
                <FiFileText style={{ fontSize: '1rem', color: lightTheme.iconColor }} /> PDF
              </span>
            </InfoItem>
            <InfoItem><span>Total Pages:</span> <span>{document.total_pages || 'N/A'}</span></InfoItem>
            <InfoItem><span>Processing Time:</span> <span>{document.processing_time != null ? `${document.processing_time}s` : 'N/A'}</span></InfoItem>
            <InfoItem><span>Created At:</span> <span>{new Date(document.created_at).toLocaleDateString()}</span></InfoItem>
            <InfoItem>
              <span>Human Verification:</span> 
              <span style={{ color: document.verified ? lightTheme.accentColorGreen : '#D97706', fontWeight: '500' }}>
                {document.verified ? 'Verified' : 'Pending'}
              </span>
            </InfoItem>
            {document.status === 'completed' && !document.verified && (
              <VerifyButton onClick={() => navigate(`/verify/${documentId}`)}>
                Proceed to Verification
              </VerifyButton>
            )}
          </Card>

          {/* Card 3: Processing Agents */}
          <Card>
            <CardTitle><FiCpu style={{color: lightTheme.accentColor}} /> Processing Agents</CardTitle>
            <VerticalProgressContainer>
              <VerticalProgressBar style={{ 
                height: `${(agents.filter(a => getAgentStatus(a.statusKey) === 'completed').length / agents.length) * 100}%` 
              }} />
              <AgentsList>
                {agents.map(agent => {
                  const agentCurrentStatus = getAgentStatus(agent.statusKey);
                  return (
                    <AgentItem key={agent.id} active={agentCurrentStatus === 'active'} completed={agentCurrentStatus === 'completed'}>
                      <div className="agent-info">
                        <span className="agent-icon">{agent.icon}</span>
                        <div>
                          <div className="agent-name">{agent.name}</div>
                          <div className="agent-detail">{agent.detail}</div>
        </div>
                    </div>
                    </AgentItem>
                  );
                })}
              </AgentsList>
            </VerticalProgressContainer>
          </Card>
          
          {/* Card 4: AI Inference */}
          <Card>
            <CardTitle><FiGitMerge style={{color: lightTheme.accentColor}} /> AI Inference</CardTitle>
            <CircularProgressWrapper>
              <CircularProgressbar
                value={confidenceScore}
                text={`${confidenceScore}%`}
                styles={buildStyles({
                  rotation: 0.25,
                  strokeLinecap: 'round',
                  textSize: '22px',
                  pathTransitionDuration: 0.5,
                  pathColor: lightTheme.accentColorGreen,
                  textColor: lightTheme.primaryText,
                  trailColor: lightTheme.borderColor,
                })}
              />
            </CircularProgressWrapper>
            <InfoItem><span>Overall Confidence:</span> <span>{confidenceScore}%</span></InfoItem>
            <InfoItem><span>ML Status:</span> <span>{document.ml_inference_status || 'N/A'}</span></InfoItem>
            <InfoItem>
              <span>Rating:</span>
              <RatingStars>
                {[...Array(5)].map((_, index) => {
                  const rating = document.rating || 0;
                  const starValue = index + 1;
                  const isFilled = starValue <= rating;
                  return (
                    <span key={index} className="star">
                      <FiStar className={isFilled ? 'filled' : 'empty'} />
                    </span>
                  );
                })}
                <span style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: lightTheme.secondaryText }}>
                  {document.rating ? `${document.rating.toFixed(1)}/5` : 'Not Rated'}
                </span>
              </RatingStars>
            </InfoItem>
          </Card>
        </TopRow>

        <BottomRow>
          <Card>
            {results?.merged_content?.shares && results.merged_content.shares.length > 0 ? (
              <>
                <CardTitle><FiFileText style={{color: lightTheme.accentColor}} /> Share Classes</CardTitle>
                <TableContainer>
                  <ScrollableTableWrapper>
                    <StyledTable>
              <thead>
                        <tr>
                          <th>Share Class</th>
                          <th>Redemption Fee</th>
                          <th>Management Fee</th>
                          <th>Min Investment</th>
                </tr>
              </thead>
              <tbody>
                {results.merged_content.shares.map((share, index) => (
                          <tr key={index}>
                            <td>{share.shareClass?.value || 'N/A'}</td>
                            <td>{share['Redemption Fee']?.value || 'N/A'}</td>
                            <td>{share['Max Management fee']?.value || 'N/A'}</td>
                            <td>{share['Minimum initial Investment amount with purchase currency']?.value || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
                    </StyledTable>
                  </ScrollableTableWrapper>
                </TableContainer>
              </>
            ) : (
              <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <CardTitle style={{ marginBottom: '0.5rem' }}><FiFileText style={{color: lightTheme.accentColor}}/> Share Classes</CardTitle>
                <span style={{ color: lightTheme.secondaryText, fontSize: '0.85rem' }}>No Share Class data available.</span>
        </div>
      )}
          </Card>

          <Card>
            {results?.merged_content?.subfunds && results.merged_content.subfunds.length > 0 ? (
              <>
                <CardTitle><FiFileText style={{color: lightTheme.accentColor}} /> Sub-Funds</CardTitle>
                <TableContainer>
                  <ScrollableTableWrapper>
                    <StyledTable>
              <thead>
                        <tr>
                          <th>Fund Name</th>
                          <th>Benchmark</th>
                </tr>
              </thead>
              <tbody>
                {results.merged_content.subfunds.map((subfund, index) => (
                          <tr key={index}>
                            <td>{subfund.fundName?.value || 'N/A'}</td>
                            <td>{subfund.benchmarkfund?.value || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
                    </StyledTable>
                  </ScrollableTableWrapper>
                </TableContainer>
              </>
            ) : (
              <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <CardTitle style={{ marginBottom: '0.5rem' }}><FiFileText style={{color: lightTheme.accentColor}} /> Sub-Funds</CardTitle>
                <span style={{ color: lightTheme.secondaryText, fontSize: '0.85rem' }}>No Sub-Fund data available.</span>
        </div>
      )}
          </Card>
        </BottomRow>
      </MainLayout>
    </MainContainer>
  );
};

export default DetailedDocumentDashboard; 